<template>
    <div>
        <v-row>
            <v-col>
                <h4 class="text-h4">Retained Earning</h4>
                <p class="text-subtitle-1">End of year closing of revenue and expense accounts</p>
                <v-divider></v-divider>
            </v-col>
        </v-row>
        <v-row class="align-center">
            <v-col cols="12" md="3">
                <v-autocomplete
                    label="Year"
                    v-model="year"
                    outlined
                    dense
                    hide-details
                    :items="years"
                ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="3">
                <v-btn color="primary" block @click="fetch">
                    <v-icon left>mdi-calculator</v-icon>
                    calculate profit/loss
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-sheet>
                    <div><v-icon left color="success">mdi-arrow-up</v-icon>Total Revenue</div>
                    <div class="text-h4" :class="{'error--text': totalIncome < 0}" >{{formatMoney(totalIncome, baseCurrency.abbreviation)}}</div>
                </v-sheet>
            </v-col>
            <v-col>
                <v-sheet>
                        <div><v-icon left color="error">mdi-arrow-down</v-icon>Total Expense</div>
                        <div class="text-h4" :class="{'error--text': totalExpense < 0}">{{formatMoney(totalExpense, baseCurrency.abbreviation)}}</div>
                    </v-sheet>
            </v-col>
            <v-col>
                <v-sheet>
                    <div>
                        <span v-if="netTotal < 0"><v-icon left>mdi-arrow-up</v-icon>Net Profit</span>
                        <span v-else-if="netTotal > 0"><v-icon left>mdi-arrow-down</v-icon>Net Loss</span>
                        <span v-else><v-icon left>mdi-swap-vertical</v-icon>Net Gain/Loss</span>
                    </div>
                    <div class="text-h4" :class="{'error--text': netTotal < 0}">{{formatMoney(netTotal, baseCurrency.abbreviation)}}</div>
                </v-sheet>
            </v-col>
        </v-row>
        <v-row>
            <v-col v-if="!loading">
                    <div class="text-h6">Revenue</div>
                <v-simple-table
                dense
                >
                    <thead>
                        <tr>
                            <th>Account</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(d, i) in revenueData" :key="i">
                            <td>{{d.account_number}} - {{d.account_name}} </td>
                            <td :class="{'error--text': d.amount < 0}" > {{ formatMoney(d.amount, baseCurrency.abbreviation) }}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="font-weight-bold surface">
                            <td>Total</td>
                            <td class="text-bold" :class="{'error--text': totalIncome< 0}">{{ formatMoney(totalIncome, baseCurrency.abbreviation)}}</td>
                        </tr>
                    </tfoot>
                </v-simple-table>
            </v-col>
            <v-col v-if="!loading">
                <div class="text-h6">Expense</div>
                <v-simple-table
                dense
                >
                    <thead>
                        <tr>
                            <th>Account</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(d, i) in expenseData" :key="i">
                            <td>{{d.account_number}} - {{d.account_name}} </td>
                            <td :class="{'error--text': d.amount < 0}" > {{ formatMoney(d.amount, baseCurrency.abbreviation)}}</td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="font-weight-bold surface">
                            <td>Total</td>
                            <td class="text-bold" :class="{'error--text': totalExpense < 0}">{{ formatMoney(totalExpense, baseCurrency.abbreviation) }}</td>
                        </tr>
                    </tfoot>
                </v-simple-table>
            </v-col>
            <v-col v-if="loading">
                <div>
                    <v-skeleton-loader :loading="loading" type="table"></v-skeleton-loader>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { formatNumber, formatMoney } from 'accounting';
import { getTotal } from '../../utils/accounting';
import {reportingPeriodProfitLose} from "./../../services/journal/retainedEarning" 
import moment from 'moment';
import { mapState } from "vuex";
export default {
    data() {
        return {
            headers: [
                { text: "Account #" , value: "account_number" },
                { text: "Account Name", value: "account_name"},
                { text: "Account Type", value: "root_type"},
                { text: "Debit", value: "amount"},
                { text: "Credit"}

            ],
            year: null,
            loading: false,
            data: []
            
        }
    },
    computed: {
        ...mapState("accounting", ["baseCurrency"]),
        years() {
            let years = [];
            let startYear = 2020;
            let noOfYears = 100;
            for(let i = 0; i < noOfYears; i++) {
                years.push(startYear + i)
            }
            return years;
        },
        totalExpense() {
            return getTotal(this.data.filter(each => each.root_type == "EXPENSE") , "amount")
        },
        totalIncome() {
            return getTotal(this.data.filter(each => each.root_type == "REVENUE") , "amount")
        },
        totalDebit() {
            return  getTotal(this.data.filter(each => each.amount > 0) , "amount")
        },
        totalCredit() {
            return  getTotal(this.data.filter(each => each.amount < 0) , "amount")
        },
        netTotal() {
            return this.totalIncome + this.totalExpense
        },
        revenueData() {
            return this.data.filter(each => each.root_type == "REVENUE")
        },
        expenseData() {
            return this.data.filter(each => each.root_type == "EXPENSE")
            
        }
    },
    methods: {
        formatNumber,
        formatMoney(amount, currency) {
            return formatMoney(amount, { symbol: currency, format: {
                pos: "%s %v",
                neg: "%s (%v)",
                zero: "%s %v"
            } })
        },
        getReportingPeriodProfitLose(date) {
            this.data = [];
            this.loading = true
            reportingPeriodProfitLose(date)
                .then(res => this.data = res.data)
                .finally(() => this.loading = false)
        },
        fetch() {
            let from = moment(`${this.year}-01-01`).format("YYYY-MM-DD")
            let to = moment(`${this.year}-12-31`).format("YYYY-MM-DD")
            this.getReportingPeriodProfitLose({ from, to })
        }
    },
    created() {
        
    }
    
} 
</script>

<style>

</style>